<template>
  <div>
    <!-- Header com busca e botão -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-md-between">
          <h4 class="mb-0 fw-bold text-dark">Meus Pacientes</h4>
          <div class="d-flex gap-2 flex-grow-1 flex-md-grow-0" style="max-width: 100%;">
            <div class="form-icon position-relative flex-grow-1" style="min-width: 200px;">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                   class="feather feather-search fea icon-sm icons position-absolute" style="left: 12px; top: 50%; transform: translateY(-50%); color: #6c757d;">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input name="search" id="search" class="form-control ps-5" 
                     placeholder="Buscar por nome..." 
                     v-model="search"
                     style="border-radius: 8px;">
            </div>
            <button type="button" class="btn btn-primary d-flex align-items-center gap-2" 
                    title="Novo paciente"
                    data-bs-target="#ModalPatient" data-bs-toggle="modal"
                    @click="editing = false; currentPatient = null; formPatient = {}">
              <i class="fas fa-plus"></i> 
              <span class="d-none d-md-inline">Novo Paciente</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
      <p class="text-muted mt-3">Carregando pacientes...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading && patients.length === 0" class="text-center py-5">
      <div class="mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" 
             stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
             class="text-muted" style="opacity: 0.5;">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      </div>
      <h5 class="text-muted mb-2">{{ search ? 'Nenhum paciente encontrado' : 'Nenhum paciente cadastrado' }}</h5>
      <p class="text-muted mb-4">{{ search ? 'Tente buscar com outro termo' : 'Comece adicionando seu primeiro paciente' }}</p>
      <button v-if="!search" type="button" class="btn btn-primary" 
              data-bs-target="#ModalPatient" data-bs-toggle="modal"
              @click="editing = false; currentPatient = null; formPatient = {}">
        <i class="fas fa-plus me-2"></i>Adicionar Primeiro Paciente
      </button>
    </div>

    <!-- Lista de Pacientes -->
    <div v-else class="row g-4">
      <div class="col-md-6" v-for="patient in patients" :key="patient.id_patient">
        <div class="card border-0 rounded-3 shadow-sm h-100 patient-card" 
             style="transition: transform 0.2s, box-shadow 0.2s;"
             @mouseenter="$event.currentTarget.style.transform = 'translateY(-4px)'; $event.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)'"
             @mouseleave="$event.currentTarget.style.transform = 'translateY(0)'; $event.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'">
          <div class="card-body">
            <!-- Foto e Informações -->
            <div class="text-center mb-4">
              <div class="position-relative d-inline-block mb-3">
                <img :src="url(patient.patient_photo)" v-if="patient.patient_photo"
                     class="rounded-circle border border-3 border-light shadow-sm" 
                     style="width: 100px; height: 100px; object-fit: cover;" 
                     alt="Foto do paciente">
                <div v-else 
                     class="rounded-circle border border-3 border-light shadow-sm d-flex align-items-center justify-content-center"
                     style="width: 100px; height: 100px; margin: 0 auto; background-color: #780f5a;">
                  <span class="text-white fw-bold" style="font-size: 2.5rem;">
                    {{ initialsName(patient.patient_name) }}
                  </span>
                </div>
                <span v-if="patient.main" 
                      class="position-absolute top-0 start-100 translate-middle badge bg-success rounded-pill"
                      style="font-size: 0.65rem;">
                  Principal
                </span>
              </div>

              <h5 class="mb-2 fw-bold text-dark">{{ patient.patient_name }}</h5>
              
              <div class="d-flex align-items-center justify-content-center gap-2 flex-wrap mb-2">
                <span v-if="patient.plan_name"
                      class="badge bg-primary bg-opacity-10 px-3 py-2 rounded-pill"
                      :class="{ 'plan-badge-clickable': patient.plan_card }"
                      style="font-size: 0.8rem; font-weight: 600; color: white"
                      :title="patient.plan_card ? 'Ver carteirinha' : null"
                      :data-bs-target="patient.plan_card ? '#ModalPlanCard' : null"
                      :data-bs-toggle="patient.plan_card ? 'modal' : null"
                      @click="patient.plan_card ? cardPatient = patient : null">
                  <i class="fas fa-tag me-1" style="font-size: 0.7rem;"></i>
                  {{ patient.plan_name }}
                  <i v-if="patient.plan_card" class="fas fa-id-card ms-1" style="font-size: 0.7rem;"></i>
                </span>
              </div>

              <p class="text-muted mb-0 small">
                <i class="fas fa-id-card me-1"></i>
                {{ patient.document_number || maskit(patient.patient_cpf) }}
              </p>
            </div>

            <!-- Ações -->
            <div class="d-grid gap-2">
              <button class="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2" 
                      data-bs-target="#ModalPatient" data-bs-toggle="modal"
                      @click="currentPatient = patient; editing = true; formPatient = patient;" 
                      title="Editar paciente">
                <i class="fas fa-edit"></i>
                <span>Editar</span>
              </button>
              <button v-if="!patient.main"
                      class="btn btn-outline-danger d-flex align-items-center justify-content-center gap-2" 
                      data-bs-target="#ConfirmDelete" data-bs-toggle="modal"
                      @click="currentPatient = patient" 
                      title="Remover paciente">
                <i class="fas fa-trash"></i>
                <span>Remover</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="modal fade" id="ModalPatient" tabindex="1" aria-hidden="true">
    <div class="modal-dialog">
      <form method="post" @submit.prevent="savePatient" ref="formPatient">
        <div class="modal-content rounded shadow border-0">
          <div class="modal-header border-bottom">
            <h5 class="modal-title">{{ editing ? 'Editar' : 'Novo' }} paciente</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-12">

                <div class="alert alert-success" role="alert" v-if="msgPatientSuccess">
                  {{ msgPatientSuccess }}
                </div>

                <div class="alert alert-danger" role="alert" v-if="msgPatientError">
                  {{ msgPatientError }}
                </div>

                <div class="mb-3">
                  <label class="form-label" for="patient_name">Nome <span class="text-danger">*</span></label>
                  <div class="form-icon position-relative">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="feather feather-user fea icon-sm icons">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    <input name="patient_name" id="patient_name" type="text" v-model="formPatient.patient_name"
                           :class="errors.patient_name ? 'is-invalid' : ''" required
                           class="form-control ps-5" placeholder="Nome :">
                    <div class="invalid-feedback d-block" v-if="errors.patient_name">
                      {{ errors.patient_name.join('\n') }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <PatientIdentityFields :form="formPatient" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label" for="patient_birthdate">Data Nascimento <span
                      class="text-danger">*</span></label>
                  <div class="form-icon position-relative">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="feather feather-mail fea icon-sm icons">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    <input name="patient_birthdate" id="patient_birthdate" type="tel"
                           v-model="formPatient.patient_birthdate" required
                           :class="errors.patient_birthdate ? 'is-invalid' : ''" v-mask="'##/##/####'"
                           class="form-control ps-5" placeholder="Data de nascimento :">
                    <div class="invalid-feedback d-block" v-if="errors.patient_birthdate">
                      {{ errors.patient_birthdate.join('\n') }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label" for="patient_gender">Sexo</label>
                  <select id="patient_gender" v-model="formPatient.patient_gender" class="form-select">
                    <option :value="null">Selecione</option>
                    <option value="Male">Masculino</option>
                    <option value="Female">Feminino</option>
                  </select>
                </div>
              </div>
              <div class="col-md-12">
                <div class="mb-3">
                  <label class="form-label">Foto</label>
                  <div class="form-icon position-relative">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="feather feather-camera fea icon-sm icons">
                      <path
                          d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                      <circle cx="12" cy="13" r="4"></circle>
                    </svg>
                    <input name="photo" id="photo" type="file" class="form-control ps-5" placeholder="Sua foto :"
                           accept="image/*" :class="errors.patient_photo ? 'is-invalid' : ''" ref="photo">
                    <div class="invalid-feedback d-block" v-if="errors.patient_photo">
                      {{ errors.patient_photo.join('\n') }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-12">
                <div class="mb-3">
                  <label class="form-label" for="observations">Observação</label>
                  <div class="form-icon position-relative">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="feather feather-message-circle fea icon-sm icons">
                      <path
                          d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                    </svg>
                    <textarea name="observations" id="observations" rows="4"
                              :class="errors.observations ? 'is-invalid' : ''" v-model="formPatient.observations"
                              class="form-control ps-5" placeholder="Observação do paciente :"></textarea>
                    <div class="invalid-feedback d-block" v-if="errors.observations">
                      {{ errors.observations.join('\n') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="submit" :disabled="submitted" class="btn btn-outline-primary">
              Salvar
              <svg v-if="submitted" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="spinner"
                   class="local-spin svg-inline--fa fa-spinner fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg"
                   viewBox="0 0 512 512">
                <path fill="currentColor"
                      d="M304 48c0 26.51-21.49 48-48 48s-48-21.49-48-48 21.49-48 48-48 48 21.49 48 48zm-48 368c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.49-48-48-48zm208-208c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.49-48-48-48zM96 256c0-26.51-21.49-48-48-48S0 229.49 0 256s21.49 48 48 48 48-21.49 48-48zm12.922 99.078c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48c0-26.509-21.491-48-48-48zm294.156 0c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48c0-26.509-21.49-48-48-48zM108.922 60.922c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.491-48-48-48z"></path>
              </svg>
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>

  <div class="modal fade" id="ModalPlanCard" tabindex="1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content rounded shadow border-0">
        <div class="modal-header border-bottom">
          <h5 class="modal-title">Carteirinha</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <PlanCard v-if="cardPatient" :patient="cardPatient"/>
          <p class="text-muted text-center small mt-3 mb-0">Clique no cartão para ver o verso</p>
        </div>
      </div>
    </div>
  </div>

  <div class="modal fade" id="ConfirmDelete" tabindex="1" aria-hidden="true">
    <div class="modal-dialog">
      <form method="post" @submit.prevent="removePatient" ref="formPatient">
        <div class="modal-content rounded shadow border-0">
          <div class="modal-header border-bottom">
            <h5 class="modal-title">Remover Paciente</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p>
              Tem certeza que deseja remover esse paciente?
            </p>
          </div>
          <div class="modal-footer">
            <button type="submit" :disabled="submitted" class="btn btn-outline-primary">
              Remover
              <svg v-if="submitted" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="spinner"
                   class="local-spin svg-inline--fa fa-spinner fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg"
                   viewBox="0 0 512 512">
                <path fill="currentColor"
                      d="M304 48c0 26.51-21.49 48-48 48s-48-21.49-48-48 21.49-48 48-48 48 21.49 48 48zm-48 368c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.49-48-48-48zm208-208c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.49-48-48-48zM96 256c0-26.51-21.49-48-48-48S0 229.49 0 256s21.49 48 48 48 48-21.49 48-48zm12.922 99.078c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48c0-26.509-21.491-48-48-48zm294.156 0c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48c0-26.509-21.49-48-48-48zM108.922 60.922c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.491-48-48-48z"></path>
              </svg>
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import PatientIdentityFields from "../../components/PatientIdentityFields.vue";
import {mask} from "vue-the-mask";
import http from "@/helpers/http";
import strMask from "@/helpers/strMask";
import str from "@/helpers/str";
import Alert from "@/helpers/alert";
import date from "@/helpers/date";
import PlanCard from "@/views/components/PlanCard.vue";

export default {
  name: "Patient",
  components: { PatientIdentityFields,
    PlanCard,
  },
  directives: {
    mask,
  },
  data() {
    return {
      search: '',
      submitted: false,
      editing: false,
      loading: false,
      patientsList: [],
      currentPatient: {},
      cardPatient: null,
      formPatient: {
        patient_name: '',
        patient_cpf: '',
        patient_birthdate: '',
        patient_gender: null,
        observations: '',
      },
      errors: [],
      msgPatientSuccess: '',
      msgPatientError: '',
    }
  },
  watch: {
    currentPatient: function (newValue) {
      if (newValue?.patient_birthdate)
        newValue.patient_birthdate = newValue.patient_birthdate_br;
    }
  },
  computed: {
    userAuth() {
      return this.$store.state.auth.user;
    },
    patients() {
      if (this.search === '')
        return this.patientsList;

      return this.patientsList.filter(patient => {
        return patient.patient_name.toLowerCase().includes(this.search.toLowerCase())
      });
    },
  },
  mounted() {
    this.loadPatients();
  },
  methods: {
      url(path) {
          return str.url(path)
      },
    maskit(value) {
      return strMask.it(value);
    },
    initialsName(name) {
      return str.initialsName(name)
    },
    loadPatients() {
      this.loading = true;
      http.get(`/users/${this.userAuth.id_user}/patients`).then((response) => {
        this.patientsList = response.data;
      }).catch((error) => {
        Alert.error('Erro ao carregar pacientes');
        console.error(error);
      }).finally(() => {
        this.loading = false;
      });
    },
    removePatient() {
      this.submitted = true;
      let modalConfirmDelete = bootstrap.Modal.getInstance(document.getElementById('ConfirmDelete'));
      
      http.delete(`/users/${this.userAuth.id_user}/patients/${this.currentPatient.id_patient}`).then(() => {
        modalConfirmDelete.hide();
        Alert.success('Paciente removido com sucesso.');
        this.loadPatients();
      }).catch((e) => {
        Alert.error(e.response?.data || 'Erro ao remover paciente');
      }).finally(() => {
        this.submitted = false;
      });
    },
    savePatient() {
      this.submitted = true;
      let userAuth = this.$store.state.auth.user;
      this.errors = [];
      this.msgPatientError = '';

      let url = `/users/${userAuth.id_user}/patients`;
      let method = 'post';
      if (this.editing) {
        url = `/users/${userAuth.id_user}/patients/${this.currentPatient.id_patient}`;
        method = 'put';
      }

      const form = new FormData();
      form.append('_method', method);
      form.append('patient_name', this.formPatient.patient_name);
      form.append('patient_cpf', this.formPatient.patient_cpf || '');
      for (const field of ['is_foreign', 'nationality', 'document_type', 'document_country', 'document_number']) {
        const value = this.formPatient[field];
        if (value != null) form.append(field, typeof value === 'boolean' ? Number(value) : value);
      }
      form.append('patient_birthdate', this.formPatient.patient_birthdate);
      if (this.formPatient.patient_gender) {
        form.append('patient_gender', this.formPatient.patient_gender);
      }
      form.append('observations', this.formPatient.observations);

      if (this.$refs.photo.files.length > 0)
        form.append('patient_photo', this.$refs.photo.files[0]);

      http({
        method: 'post',
        url: url,
        data: form
      }).then((_) => {
        this.msgPatientSuccess = 'Paciente ' + (this.editing ? 'atualizado' : 'cadastrado') + ' com sucesso.';
        let modal = bootstrap.Modal.getInstance(document.getElementById('ModalPatient'));
        modal.hide();
        this.loadPatients();
        this.formPatient = {
          patient_name: '',
          patient_cpf: '',
          patient_birthdate: '',
          observations: '',
        };
        this.errors = [];
        this.msgPatientError = '';
        Alert.success(this.msgPatientSuccess);
      }).catch((error) => {
        let response = error.response;
        if (response.data?.errors) {
          this.errors = response.data.errors;
        } else {
          this.msgPatientError = response.data;
        }
      }).finally(() => {
        this.submitted = false;
      });

    }
  }
}
</script>

<style scoped type="text/scss">
@import '~sweetalert2/dist/sweetalert2.css';

.patient-card {
  cursor: default;
}

.patient-card:hover {
  cursor: default;
}

.plan-badge-clickable {
  cursor: pointer;
}

.form-icon .icons {
  pointer-events: none;
}

.local-spin {
  animation: spin 1s linear infinite;
  display: inline-block;
  margin-left: 8px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .patient-card {
    margin-bottom: 1rem;
  }
}
</style>